import React, { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { Grid, Container, Paper, Theme, styled } from '@quillbot/design-system';
import { makeStyles } from '@mui/styles';
import withQuillLayout from 'src/_common/hoc/QuillLayout';
import TopControlBar from 'src/grammar-checker/component/TopControlBar';
import { RootState } from 'src/_common/models/redux/store';
import { useDispatch, useSelector } from 'react-redux';
import GrammarCheckerExplanation from 'src/grammar-checker/component/GrammarCheckerExplanation';
import Head from 'next/head';
import QuillLocalStorage from 'core/utils/localStorageClient';
import { TinyMCELogo } from '@Common/quillElements/icon';
import { handleHotKeys } from 'src/grammar-checker/handlers/hotkeys';
import ExtensionUpsale from '@Common/components/ExtensionUpsale';
import { useRouter } from 'next/router';
import ProductUpSellModal from '@Common/components/ProductUpSellModal';
import QuillSessionStorage from 'core/utils/sessionStorageClient';
import { hydrateStoreFromSession } from 'src/grammar-checker/actions/controlActions';

const EditorBox = dynamic(() => import('src/grammar-checker/component/EditorBox'), { ssr: false });
const BottomControlBar = dynamic(() => import('src/grammar-checker/component/BottomControlBar'), {
  ssr: false,
});
const GrammarCheckerDrawerControlBar = dynamic(
  () => import('src/grammar-checker/component/GrammarCheckerDrawerControlBar'),
  {
    ssr: false,
  }
);

const useStyles = makeStyles<Theme, { isDrawerOpen: boolean; independentTool: boolean }>(
  (theme) => ({
    bgColorbody: {
      backgroundColor: theme.palette.background.default,
      paddingBottom: '20px !important',
      paddingLeft: (props) => (props.isDrawerOpen ? '24px' : '42px'),
      paddingRight: (props) => (props.isDrawerOpen ? '24px' : '42px'),
      [theme.breakpoints.down('sm')]: {
        paddingLeft: '24px',
        paddingRight: '24px',
      },
    },
    grammarCheckerHeader: {
      position: 'sticky',
      top: (props) => (props.independentTool ? 0 : 49),
      borderBottom: '1px solid',
      borderBottomColor: theme.palette.divider,
      zIndex: 1,
      backgroundColor: theme.palette.grey[50],
    },

    grammarCheckerFooter: {
      backgroundColor: theme.palette.grey[50],
      position: 'sticky',
      bottom: 0,
      zIndex: 1,
    },
    grammarCheckerExplanation: {
      backgroundColor: theme.palette.grey[50],
    },
  })
);

// this schema is for SEO
const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'How does QuillBot’s Grammar Checker tool work?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '1. Either copy and paste your text into the text box or begin typing. 2. Click on each underlined error to view the replacement options. 3. Replace each error one at a time, or hit the “Fix All Errors” button.',
      },
    },
    {
      '@type': 'Question',
      name: 'Who can use QuillBot’s Grammar Checker?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Anyone can use QuillBot’s Grammar Checker (and we hope you do). It’s particularly great for students who need to double check their work before handing it in, for professionals who are sending formal correspondence, and for those who are learning English to get acquainted with English grammar rules.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can this tool be used as a spell checker?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Absolutely! Our Grammar Checker doubles as a spell check tool to ensure that every part of every sentence you write is correct. It can be hard to keep track of all the moving parts of a writing project, but now you can cross correct spelling off that list━we’ve got it covered.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can this tool be used as a punctuation checker?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Our Grammar Checker sure can be used as a punctuation check. Using the correct punctuation is of the utmost importance when trying to communicate effectively, so we know how necessary it is that our tool functions as a grammar and punctuation check.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is QuillBot’s Grammar Checker tool free?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes! Our Grammar Checker is completely free to use. There is no sign up or payment required.',
      },
    },
  ],
};
// this schema is for SEO

const StyledRootContainer = styled(Container)<{ independentTool: boolean }>`
  && {
    padding-top: 25px;
    padding-bottom: 30px;
    flex-direction: column;
    flex-wrap: unset !important;
    display: flex;
    padding: ${({ independentTool }: { independentTool: boolean }) =>
      independentTool ? '0px !important' : ''};
  }
`;

function Editor() {
  const { isMobile, native, isChromeExtInstalled } = useSelector(
    (state: RootState) => state.common.platform
  );
  const isExtension = useSelector((state: RootState) => state.common.platform.isExtension) || false;
  const extensionName = useSelector(
    (state: RootState) => state.common.platform.extensionMetaData?.type
  );
  const independentTool = useSelector((state: RootState) => state.common.independentTool);

  const quillDrawer = useSelector((state: RootState) => state.common.quillDrawer);
  const [showExtensionUpsellBanner, setShowExtensionUpsellBanner] = useState(false);

  const router = useRouter();
  const dispatch = useDispatch();

  // used this for extension & mobile only
  let editorBoxContainerHeight = 'calc(100vh - 88px)';
  if (isExtension) {
    if (extensionName === 'chrome') editorBoxContainerHeight = 'calc(100vh - 93px)';
    else if (extensionName === 'google-docs') editorBoxContainerHeight = 'calc(100vh - 93px)';
    else if (extensionName === 'ms-word') editorBoxContainerHeight = 'calc(100vh - 93px)';
  }

  if (isMobile && independentTool) {
    editorBoxContainerHeight = 'calc(100vh - 111px)';
  }

  useEffect(() => {
    try {
      const grmrStore: any = JSON.parse(QuillSessionStorage.getInstance().get('grmr', '{}'));
      QuillSessionStorage.getInstance().delete('grmr');
      if (grmrStore && Object.keys(grmrStore).length) {
        if (grmrStore?.input && Object.keys(grmrStore?.input).length) {
          dispatch(hydrateStoreFromSession('INPUT', grmrStore?.input));
        }
        if (grmrStore?.controls && Object.keys(grmrStore?.controls).length) {
          dispatch(hydrateStoreFromSession('CONTROL', grmrStore?.controls));
        }
        if (grmrStore?.settings && Object.keys(grmrStore?.settings).length) {
          dispatch(hydrateStoreFromSession('SETTINGS', grmrStore?.settings));
        }
      }
      // eslint-disable-next-line no-empty
    } catch (e) {}
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleHotKeys);
    return () => {
      document.removeEventListener('keydown', handleHotKeys);
    };
  }, []);

  useEffect(() => {
    if (isExtension) QuillLocalStorage.getInstance().set('visitedGC', 'true');
  }, [isExtension]);

  useEffect(() => {
    if (typeof isChromeExtInstalled === 'boolean') {
      const upsellBannerState =
        QuillLocalStorage.getInstance().get('showExtensionUpsellBanner', 'true') === 'true' &&
        !isChromeExtInstalled &&
        !independentTool;
      setShowExtensionUpsellBanner(upsellBannerState);
    }
  }, [isChromeExtInstalled]);

  const onBannerCloseIconClick = () => {
    QuillLocalStorage.getInstance().set('showExtensionUpsellBanner', 'false');
    setShowExtensionUpsellBanner(false);
  };

  const classes = useStyles({ isDrawerOpen: !!quillDrawer, independentTool });
  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          key="company-schema"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      </Head>
      {!isMobile && !isExtension && (
        <>
          <StyledRootContainer
            maxWidth={false}
            className={`root-container home-container-gray ${classes.bgColorbody}`}
            independentTool={independentTool}
          >
            <Grid container alignItems="center" justifyContent="center">
              <Grid item xs={12} className="mid-container">
                {showExtensionUpsellBanner && (
                  <ExtensionUpsale
                    onBannerCloseIconClick={onBannerCloseIconClick}
                    onBannerAddClick={() => setShowExtensionUpsellBanner(false)}
                    originComp="bannerGC"
                  />
                )}

                <Paper
                  elevation={independentTool ? 0 : 3}
                  style={{
                    borderRadius: '0px',
                    margin: independentTool ? '' : '12px auto',
                  }}
                >
                  <Grid container>
                    <Grid
                      container
                      className={classes.grammarCheckerHeader}
                      // style={{
                      //   position: 'sticky',
                      //   top: 0,
                      //   backgroundColor: 'white',
                      //   zIndex: 1,
                      // }}
                      justifyContent="center"
                    >
                      <TopControlBar showExtensionUpsellBanner={showExtensionUpsellBanner} />
                      {/* <hr
                      style={{
                        margin: 0,
                        borderLeft: 'none',
                        borderRight: 'none',
                        borderTop: '1px',
                        width: '100%',
                      }}
                    /> */}
                    </Grid>
                    <Grid
                      container
                      style={{ minHeight: independentTool ? 'calc(100vh - 130px)' : '50vh' }}
                    >
                      <EditorBox isMobile={isMobile} isExtension={isExtension} />
                    </Grid>
                    <Grid
                      container
                      className={classes.grammarCheckerFooter}
                      // style={{
                      //   position: 'sticky',
                      //   bottom: 0,
                      //   backgroundColor: 'white',
                      //   zIndex: 1,
                      // }}
                      justifyContent="center"
                    >
                      <BottomControlBar />
                    </Grid>
                  </Grid>
                </Paper>
                {!independentTool && (
                  <Grid container justifyContent="flex-end">
                    <a
                      rel="noopener noreferrer"
                      target="_blank"
                      href="https://www.tiny.cloud/"
                      style={{ maxHeight: '20px' }}
                    >
                      <TinyMCELogo style={{ width: '2em' }} />
                    </a>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </StyledRootContainer>
          <ProductUpSellModal variant="plac" />
        </>
      )}

      {(isMobile || isExtension) && (
        <Grid container className="nd-mobile-container" alignItems="center">
          <Grid container direction="column">
            <Grid
              item
              style={{
                position: 'sticky',
                top: 0,
                zIndex: 9,
              }}
            >
              <Grid container direction="column">
                <Grid item>
                  <TopControlBar />
                </Grid>
              </Grid>
            </Grid>

            <Grid
              item
              style={{
                minHeight: editorBoxContainerHeight,
              }}
            >
              <EditorBox isMobile={isMobile} isExtension={isExtension} />
            </Grid>
            <Grid item style={{ position: 'sticky', bottom: 0, width: '100%' }}>
              <BottomControlBar />
            </Grid>
            <Grid
              container
              justifyContent="flex-end"
              style={{ paddingTop: '10px', paddingRight: '10px' }}
            >
              <a
                rel="noopener noreferrer"
                target="_blank"
                href="https://www.tiny.cloud/"
                style={{ maxHeight: '20px' }}
              >
                <TinyMCELogo style={{ width: '2.25em' }} />
              </a>
            </Grid>
          </Grid>
        </Grid>
      )}
      {!native && !isExtension && !independentTool && (
        <Grid container className={classes.grammarCheckerExplanation}>
          {/* <GrammarCheckerDescription /> */}
          <GrammarCheckerExplanation />
        </Grid>
      )}
    </>
  );
}

export default withQuillLayout({
  children: Editor,
  drawerProp: GrammarCheckerDrawerControlBar,
});
